import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Flags } from 'constants/flags';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useEpisodeId } from 'hooks/useEpisodeId';
import { useSharedRecordTabsActions, useSharedRecordTabsStore } from 'stores/sharedRecordTabsStore';

import { buildSharedRecordTabs, SharedRecordTab } from './buildSharedRecordTabs';

type UseSharedRecordTabsResult = {
  displayTabs: SharedRecordTab[];
  activeLocationEpisodeId: string | undefined;
  handleTabClick: (tab: SharedRecordTab) => void;
};

export default function useSharedRecordTabs(): UseSharedRecordTabsResult {
  const { locationEpisode } = useSelectedLocationEpisode();
  const { episode } = useEpisode();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const episodeId = useEpisodeId();

  const cachedTabs = useSharedRecordTabsStore((s) => s.tabs);
  const cachedActiveId = useSharedRecordTabsStore((s) => s.activeLocationEpisodeId);
  const { setTabs, clear } = useSharedRecordTabsActions();

  const cachedTabsRelevant = cachedTabs.length > 0 && cachedTabs.some((t) => t.episodeId === episodeId);
  const relevantCachedTabs = cachedTabsRelevant ? cachedTabs : [];

  useEffect(() => {
    if (!cachedTabsRelevant && cachedTabs.length > 0) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedTabsRelevant]);

  const freshTabs = useMemo(() => {
    if (!locationEpisode || !episode) return [];
    if (!profile.hasFlag(Flags.SharedRecords)) return [];
    if (profile.canActAsManager && !profile.canActAsProvider) return [];
    if (locationEpisode.sharedRecords.length === 0) return [];
    if (locationEpisode.archived) return [];

    return buildSharedRecordTabs(locationEpisode, episode.id);
  }, [locationEpisode, episode, profile]);

  useEffect(() => {
    if (freshTabs.length === 0) {
      if (relevantCachedTabs.length > 0) clear();
      return;
    }

    if (relevantCachedTabs.length === 0) return;

    const cachedIdInFreshTabs = freshTabs.some((t) => t.locationEpisodeId === cachedActiveId);
    if (cachedIdInFreshTabs) {
      setTabs(freshTabs, cachedActiveId);
    } else {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshTabs]);

  const displayTabs: SharedRecordTab[] = freshTabs.length > 0 ? freshTabs : relevantCachedTabs;
  const activeLocationEpisodeId = locationEpisode?.id ?? cachedActiveId;

  const handleTabClick = (tab: SharedRecordTab) => {
    setTabs(displayTabs, tab.locationEpisodeId);
    navigate(`/patients/${tab.episodeId}?locationEpisodeId=${tab.locationEpisodeId}`);
  };

  return { displayTabs, activeLocationEpisodeId, handleTabClick };
}
